// src/utils/appealQuestions.ts
// ──────────────────────────────────────────────────────────────
// Appeal-specific dynamic questions.
//
// These questions are shown when the user is in appeal mode.
// They supplement the existing dynamic questions from
// dynamicQuestions.ts with appeal-specific context gathering.
// ──────────────────────────────────────────────────────────────

import type { DynamicQuestion } from "./dynamicQuestions";

// ── Appeal question generator ────────────────────────────────

export function generateAppealQuestions(
  formData: Record<string, string>,
  denialReason: string
): DynamicQuestion[] {
  const questions: DynamicQuestion[] = [];

  // ── Universal appeal questions (always shown) ────────────

  questions.push({
    id: "denial_text",
    question: "Paste the denial language from the insurer's letter",
    placeholder:
      "e.g., 'The requested service does not meet medical necessity criteria per our clinical policy...'",
    type: "textarea",
    priority: "high",
    category: "Denial Details",
    suggestions: [
      "Check the denial letter or EOB",
      "Look for the 'Reason for Denial' section",
      "Include any criteria references they cited",
    ],
    showWhen: () => true,
  });

  questions.push({
    id: "denial_date",
    question: "When was the denial issued?",
    placeholder: "e.g., 2025-03-15",
    type: "text",
    priority: "high",
    category: "Denial Details",
    showWhen: () => true,
  });

  questions.push({
    id: "original_submission_summary",
    question:
      "What was included in the original PA submission? (helps us avoid repeating the same arguments)",
    placeholder:
      "e.g., Submitted clinical notes, PT records showing 8 weeks completed, A1c of 9.2%, prior trial of metformin and glipizide",
    type: "textarea",
    priority: "medium",
    category: "Prior Submission",
    suggestions: [
      "Clinical notes submitted",
      "Lab results included",
      "Prior auth letter from another provider",
      "Physical therapy records",
      "Imaging reports",
    ],
    showWhen: () => true,
  });

  // ── Denial-reason-specific questions ─────────────────────

  if (denialReason === "Not medically necessary") {
    questions.push({
      id: "appeal_new_evidence",
      question:
        "Any new clinical evidence since the original submission? (new labs, imaging, symptoms, functional decline)",
      placeholder:
        "e.g., Repeat MRI shows 3mm disc progression, new left foot drop on exam, EMG confirms L5 radiculopathy",
      type: "textarea",
      priority: "high",
      category: "Escalation Evidence",
      showWhen: () => true,
    });

    questions.push({
      id: "appeal_specialist_support",
      question:
        "Has a specialist provided a supporting opinion or letter?",
      placeholder:
        "e.g., Neurosurgery consult recommends surgical intervention, letter from Dr. Smith attached",
      type: "textarea",
      priority: "medium",
      category: "Escalation Evidence",
      showWhen: () => true,
    });
  }

  if (denialReason === "Step therapy required") {
    questions.push({
      id: "appeal_step_therapy_detail",
      question:
        "For each required step therapy drug: what was tried, at what dose, for how long, and why it failed?",
      placeholder:
        "e.g., Metformin 2000mg/day x 8 months → persistent GI intolerance (nausea, diarrhea); Glipizide 10mg BID x 4 months → 2 hypoglycemic episodes requiring ED visit",
      type: "textarea",
      priority: "high",
      category: "Step Therapy Documentation",
      showWhen: () => true,
    });

    questions.push({
      id: "appeal_contraindication",
      question:
        "Are any of the required step therapy drugs contraindicated for this patient?",
      placeholder:
        "e.g., Metformin contraindicated due to eGFR 28 (CKD Stage 4); TZDs contraindicated due to CHF history",
      type: "textarea",
      priority: "high",
      category: "Step Therapy Documentation",
      showWhen: () => true,
    });
  }

  if (denialReason === "Does not meet clinical criteria") {
    questions.push({
      id: "appeal_criteria_requested",
      question:
        "Did you contact the insurer about which criteria they're applying?",
      placeholder:
        "e.g., Called UM dept — they referenced 'clinical policy bulletin 0521' requiring 12 weeks of conservative tx. We documented 14 weeks.",
      type: "textarea",
      priority: "medium",
      category: "Criteria Challenge",
      showWhen: () => true,
    });

    questions.push({
      id: "appeal_new_evidence",
      question:
        "Any new clinical findings, worsening symptoms, or failed treatments since the original submission?",
      placeholder:
        "e.g., New left foot drop on exam, completed 8 weeks PT with no improvement, positive SLR at 30 degrees bilaterally",
      type: "textarea",
      priority: "high",
      category: "Criteria Challenge",
      showWhen: () => true,
    });
  }

  if (denialReason === "Non-formulary medication") {
    questions.push({
      id: "appeal_formulary_failures",
      question:
        "Which formulary alternatives were tried?",
      placeholder: "",
      type: "textarea", // Rendered as structured rows in NewCase.tsx when appealMode
      priority: "high",
      category: "Formulary Exception",
      showWhen: () => true,
    });

    questions.push({
      id: "appeal_unique_mechanism",
      question:
        "Why can't this patient use the formulary alternatives?",
      placeholder:
        "e.g., Different mechanism of action needed after failing 2 TNF inhibitors; or: allergic reaction to formulary option",
      type: "textarea",
      priority: "medium",
      category: "Formulary Exception",
      showWhen: () => true,
    });
  }

  // Experimental/investigational: No denial-specific questions needed.
  // The edge function's denial tactics handle FDA status, NCCN compendium,
  // and clinical trial data. The physician provides treatment history
  // in the clinical context field.

  if (denialReason === "Missing documentation") {
    questions.push({
      id: "appeal_docs_previously_sent",
      question:
        "Was the documentation previously submitted? Do you have proof of submission?",
      placeholder:
        "e.g., Faxed clinical notes on 3/10 (fax confirmation #4521), uploaded to portal on 3/12 with timestamp screenshot",
      type: "textarea",
      priority: "high",
      category: "Documentation",
      showWhen: () => true,
    });

    questions.push({
      id: "appeal_docs_now_available",
      question:
        "What additional documentation are you including with this appeal?",
      placeholder:
        "e.g., Complete office notes from last 3 visits, lab results from 3/5, PT discharge summary, specialist consult note",
      type: "textarea",
      priority: "high",
      category: "Documentation",
      showWhen: () => true,
    });
  }

  if (
    denialReason === "Site of care / Facility denial" ||
    denialReason === "Level of Care"
  ) {
    questions.push({
      id: "appeal_clinical_factors",
      question:
        "What clinical factors require this care setting/level? (comorbidities, procedure complexity, safety concerns)",
      placeholder:
        "e.g., BMI 42, OSA on CPAP, prior difficult intubation, cardiac stent 2024 on dual antiplatelet — requires hospital OR with anesthesiology and cardiac backup",
      type: "textarea",
      priority: "high",
      category: "Setting Justification",
      showWhen: () => true,
    });
  }

  if (denialReason === "Too soon since last service") {
    questions.push({
      id: "appeal_interval_change",
      question:
        "What has changed clinically since the prior service that necessitates this repeat/follow-up?",
      placeholder:
        "e.g., New onset left-sided weakness since last MRI 4 months ago, concerning for disease progression vs. new lesion",
      type: "textarea",
      priority: "high",
      category: "Clinical Change",
      showWhen: () => true,
    });
  }

  if (denialReason === "Wrong code/authorization pathway") {
    questions.push({
      id: "appeal_coding_detail",
      question:
        "What CPT/ICD-10 codes were submitted, and what does the insurer say is incorrect?",
      placeholder:
        "e.g., Submitted CPT 27447 (TKA) with ICD M17.11. Insurer says PA not required for this code but denied claim anyway.",
      type: "textarea",
      priority: "high",
      category: "Coding/Pathway",
      showWhen: () => true,
    });
  }

  return questions;
}

// ── Helper: Get appeal question IDs for a denial reason ──────

export function getAppealFieldKeys(denialReason: string): string[] {
  const base = ["denial_text", "denial_date", "original_submission_summary"];

  const denialSpecific: Record<string, string[]> = {
    "Not medically necessary": ["appeal_new_evidence", "appeal_specialist_support"],
    "Step therapy required": ["appeal_step_therapy_detail", "appeal_contraindication"],
    "Does not meet clinical criteria": ["appeal_new_evidence", "appeal_criteria_requested"],
    "Non-formulary medication": ["appeal_formulary_failures", "appeal_unique_mechanism"],
    "Experimental/investigational": [],
    "Missing documentation": ["appeal_docs_previously_sent", "appeal_docs_now_available"],
    "Site of care / Facility denial": ["appeal_clinical_factors"],
    "Level of Care": ["appeal_clinical_factors"],
    "Too soon since last service": ["appeal_interval_change"],
    "Wrong code/authorization pathway": ["appeal_coding_detail"],
  };

  return [...base, ...(denialSpecific[denialReason] || [])];
}